/* eslint-disable react/no-unused-prop-types */
import * as React from 'react'
import { useNavigate } from 'react-router-dom'

import { type Icon } from '@factorialco/deprecated-design-system'
import sortBy from 'lodash/sortBy'

import { observer } from 'mobx-react'

import PanesPadForm from 'modules/core/components/PanesPadForm'
import SortableBoxList from 'modules/core/components/SortableBoxList'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import i18n from 'modules/core/lib/i18n'

import type Schema from 'modules/custom_resources/store/models/customResources/Schema'

import Header from './_Header'
import { Item } from './_Item'
import NewModal from './_NewModal'

type NewModalProps = React.ComponentProps<typeof NewModal>

interface Props {
  title?: string
  description?: string
  illustration?: React.ComponentProps<typeof Icon.Large>['icon']
  emptyMessage?: string
  addSchemaLabel?: string
  modalIllustration?: React.ComponentProps<typeof Icon.Large>['icon']
  modalTitle?: string
  modalDescription?: string
  modalNameLabel?: string
  modalNamePlaceholder?: string
  modalSubmitButton?: string
  schemas: Schema[]
  canManageTables: boolean
  createSchema: (name: string, topicName?: string) => Promise<number | undefined>
  reorderSchemas: (config: { id: string; newPosition: number }) => void
}

const CustomResources = ({
  title = i18n.t('settings.custom_resources.title'),
  description = i18n.t('settings.custom_resources.description'),
  illustration = 'table-file',
  emptyMessage = i18n.t('settings.custom_resources.blank_slate.description'),
  addSchemaLabel = i18n.t('settings.custom_resources.create'),
  modalIllustration = 'table-file',
  modalTitle = i18n.t('settings.custom_resources.new_modal.title'),
  modalDescription = i18n.t('settings.custom_resources.new_modal.description'),
  modalNameLabel = i18n.t('settings.custom_resources.new_modal.name_label'),
  modalNamePlaceholder = i18n.t('settings.custom_resources.new_modal.name_placeholder'),
  modalSubmitButton = i18n.t('settings.custom_resources.new_modal.submit_button'),
  schemas,
  canManageTables,
  createSchema,
  reorderSchemas,
}: Props) => {
  const { navigation } = useNavigation()

  const navigate = useNavigate()
  const [showModal, setShowModal] = React.useState(false)

  const onSubmit: NewModalProps['onSubmit'] = async (data) => {
    const schemaId = await createSchema(data)
    // FIXME: Hack alert!
    // The modal close and navigation happen on the same render. This makes the
    // SectionContainer animation breaks, since it never destroys from the DOM the
    // element containing the modal. This makes sure the modal gets destroyed before
    // navigating.
    schemaId &&
      setTimeout(
        () =>
          navigate(navigation.settings.customizations.customTablesId.fullPath({ id: schemaId })),
        0
      )
  }

  const items = sortBy(schemas, (schema) => schema.attributes.position)

  const updatePosition = (items: Schema[]) => {
    items.forEach((item, index) =>
      reorderSchemas({
        id: item.attributes.id,
        newPosition: index,
      })
    )
  }

  return (
    <>
      <PanesPadForm direction="horizontal">
        <Header
          illustration={illustration}
          title={title}
          description={description}
          addSchemaLabel={addSchemaLabel}
          toggleModal={() => setShowModal(true)}
          canManageTables={canManageTables}
        />
        <SortableBoxList
          items={items}
          onSortEnd={updatePosition}
          emptyMessage={emptyMessage}
          itemAsKey={(item) => item.attributes.id}
          renderItem={(item) => <Item schema={item} />}
        />
      </PanesPadForm>

      {showModal && (
        <NewModal
          illustration={modalIllustration}
          title={modalTitle}
          description={modalDescription}
          nameLabel={modalNameLabel}
          namePlaceholder={modalNamePlaceholder}
          submitButton={modalSubmitButton}
          onClose={() => setShowModal(false)}
          onSubmit={onSubmit}
        />
      )}
    </>
  )
}

export default observer(CustomResources)
